import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  Filter,
  Loader2,
  Search,
  Shield,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { useTranslation } from '@/components/i18n/Translator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

interface EurlexResult {
  celex: string;
  title: string;
  date: string;
  in_force: string;
  document_type: string;
  document_type_code: string;
  author: string;
  url: string;
  cellar_uri: string;
}

interface SearchResponse {
  results: EurlexResult[];
  page: number;
  page_size: number;
  count: number;
  error?: string;
}

interface DocumentMetadata {
  celex: string;
  title: string;
  date: string;
  in_force: string;
  document_type: string;
  document_type_code: string;
  authors: string[];
  eli: string;
  subjects: string[];
  cellar_uri: string;
  html_url: string;
  pdf_url: string;
  eurlex_url: string;
  error?: string;
}

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    if (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    ) {
      return 'http://localhost:8000';
    }
    return window.location.origin;
  }
  return 'http://localhost:8000';
};

const EurlexDashboard = () => {
  const [keyword, setKeyword] = useState('');
  const [docType, setDocType] = useState('all-types');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [inForce, setInForce] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [types, setTypes] = useState<Record<string, string>>({});
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const [selectedDoc, setSelectedDoc] = useState<EurlexResult | null>(null);
  const [docMeta, setDocMeta] = useState<DocumentMetadata | null>(null);
  const [docHtml, setDocHtml] = useState<string | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const [activeTab, setActiveTab] = useState<'meta' | 'text'>('meta');

  const pageSize = 20;

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/chat/api/eurlex/types`, {
        credentials: 'include'
      });
      if (res.ok) {
        setTypes(await res.json());
      }
    } catch {
      // non-fatal
    }
  };

  const performSearch = async (p: number = 1) => {
    setLoading(true);
    setError(null);
    setPage(p);

    const base = getApiBaseUrl();
    const params = new URLSearchParams({
      page: String(p),
      page_size: String(pageSize)
    });
    if (keyword) params.set('q', keyword);
    if (docType && docType !== 'all-types') params.set('type', docType);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (inForce) params.set('in_force', 'true');

    try {
      const res = await fetch(`${base}/chat/api/eurlex/search?${params}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Search failed');
      const data: SearchResponse = await res.json();
      if (data.error) throw new Error(data.error);
      setResults(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const viewDocument = async (doc: EurlexResult) => {
    setSelectedDoc(doc);
    setDocMeta(null);
    setDocHtml(null);
    setLoadingDoc(true);
    setLoadingHtml(true);
    setActiveTab('meta');

    const base = getApiBaseUrl();

    // Fetch metadata and HTML in parallel
    const metaPromise = fetch(
      `${base}/chat/api/eurlex/document/${encodeURIComponent(doc.celex)}/metadata`,
      { credentials: 'include' }
    ).then((r) => r.json()).catch(() => null);

    const htmlPromise = fetch(
      `${base}/chat/api/eurlex/proxy?celex=${encodeURIComponent(doc.celex)}&lang=SV`,
      { credentials: 'include' }
    ).then((r) => r.json()).catch(() => null);

    const [metaData, htmlData] = await Promise.all([metaPromise, htmlPromise]);

    setLoadingDoc(false);
    setLoadingHtml(false);

    setDocMeta(metaData ?? {
      celex: doc.celex, title: doc.title, date: doc.date,
      in_force: doc.in_force, document_type: doc.document_type,
      document_type_code: doc.document_type_code,
      authors: doc.author ? [doc.author] : [],
      eli: '', subjects: [], cellar_uri: doc.cellar_uri,
      html_url: doc.url,
      pdf_url: `https://eur-lex.europa.eu/legal-content/EN/TXT/PDF/?uri=CELEX:${doc.celex}`,
      eurlex_url: doc.url,
      error: 'Failed to load metadata'
    });

    if (htmlData?.html) {
      setDocHtml(htmlData.html);
    }
  };

  const closeDocument = () => {
    setSelectedDoc(null);
    setDocMeta(null);
    setDocHtml(null);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(1);
  };

  const { t } = useTranslation();

  const isInForce = (val: string) =>
    val === 'true' || val === 'true^^http://www.w3.org/2001/XMLSchema#boolean';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="h-6 w-6" />
          {t('eurlex.title')}
        </h2>
        <p className="text-gray-600 mt-1">{t('eurlex.description')}</p>
      </div>

      {/* Search Form */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="eurlex-keyword">{t('eurlex.form.keyword')}</Label>
                <Input
                  id="eurlex-keyword"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder={t('eurlex.form.keywordPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('eurlex.form.documentType')}</Label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('eurlex.form.allTypes')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-types">{t('eurlex.form.allTypes')}</SelectItem>
                    {Object.entries(types).map(([code, label]) => (
                      <SelectItem key={code} value={code}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Advanced filters toggle */}
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? t('eurlex.form.hideFilters') : t('eurlex.form.showFilters')}
            </button>

            {showFilters && (
              <Card className="bg-gray-50">
                <CardHeader>
                  <CardTitle className="text-base">{t('eurlex.form.advancedFilters')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="eurlex-from">
                        <Calendar className="h-3 w-3 inline mr-1" />
                        {t('eurlex.form.dateFrom')}
                      </Label>
                      <Input
                        id="eurlex-from"
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="eurlex-to">
                        <Calendar className="h-3 w-3 inline mr-1" />
                        {t('eurlex.form.dateTo')}
                      </Label>
                      <Input
                        id="eurlex-to"
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="eurlex-inforce"
                      checked={inForce}
                      onCheckedChange={(v) => setInForce(v as boolean)}
                    />
                    <div>
                      <Label htmlFor="eurlex-inforce" className="text-sm font-medium">
                        {t('eurlex.form.inForceOnly')}
                      </Label>
                      <p className="text-xs text-gray-500">
                        {t('eurlex.form.inForceDescription')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Button type="submit" disabled={loading} className="w-full md:w-auto">
              <Search className="h-4 w-4 mr-2" />
              {loading ? t('eurlex.actions.searching') : t('eurlex.actions.search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="flex items-center justify-center py-8 gap-2">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span>{t('eurlex.loading.sparql')}</span>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6 text-red-700">
            Error: {error}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results && !loading && (
        <>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-4 pb-4">
              {t('eurlex.results.pageInfo', { count: results.count, page: results.page })}
              {results.count === pageSize && (
                <span className="text-gray-500 text-sm ml-2">
                  {t('eurlex.results.moreAvailable')}
                </span>
              )}
            </CardContent>
          </Card>

          {results.results.length > 0 ? (
            <div className="space-y-4">
              {results.results.map((doc, idx) => (
                <Card
                  key={doc.celex || doc.cellar_uri || idx}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardContent className="pt-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {doc.celex && (
                            <Badge
                              variant="outline"
                              className="font-mono text-xs bg-blue-50 text-blue-800 border-blue-200"
                            >
                              {doc.celex}
                            </Badge>
                          )}
                          {doc.document_type && (
                            <Badge variant="secondary" className="text-xs">
                              {doc.document_type}
                            </Badge>
                          )}
                          {isInForce(doc.in_force) && (
                            <Badge className="text-xs bg-green-100 text-green-800 border-green-200">
                              <Shield className="h-3 w-3 mr-1" />
                              {t('eurlex.document.inForce')}
                            </Badge>
                          )}
                        </div>

                        <p className="font-medium text-gray-900 leading-snug mb-2">
                          {doc.title}
                        </p>

                        <div className="text-sm text-gray-500 flex flex-wrap gap-4">
                          {doc.date && (
                            <span>
                              <Calendar className="h-3 w-3 inline mr-1" />
                              {doc.date}
                            </span>
                          )}
                          {doc.author && (
                            <span>{t('eurlex.document.author', { author: doc.author })}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4">
                      {doc.celex && (
                        <Button size="sm" onClick={() => viewDocument(doc)}>
                          <FileText className="h-4 w-4 mr-1" />
                          {t('eurlex.actions.viewDocument')}
                        </Button>
                      )}
                      {doc.url && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={doc.url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4 mr-1" />
                            {t('eurlex.title')}
                          </a>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Pagination */}
              <div className="flex justify-center items-center gap-2 py-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => performSearch(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('eurlex.results.previous')}
                </Button>
                <span className="text-sm text-gray-600 px-2">
                  {t('eurlex.results.pageLabel', { page })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={results.count < pageSize}
                  onClick={() => performSearch(page + 1)}
                >
                  {t('eurlex.results.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-8 text-gray-500">
                {t('eurlex.results.noResults')}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Document viewer modal */}
      {selectedDoc && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={closeDocument}
        >
          <div
            className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-start justify-between p-6 border-b">
              <div className="pr-4">
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedDoc.celex && (
                    <Badge
                      variant="outline"
                      className="font-mono text-xs bg-blue-50 text-blue-800 border-blue-200"
                    >
                      {selectedDoc.celex}
                    </Badge>
                  )}
                  {selectedDoc.document_type && (
                    <Badge variant="secondary" className="text-xs">
                      {selectedDoc.document_type}
                    </Badge>
                  )}
                </div>
                <h2 className="text-lg font-semibold text-gray-900 leading-snug">
                  {selectedDoc.title}
                </h2>
                {selectedDoc.date && (
                  <p className="text-sm text-gray-500 mt-1">{selectedDoc.date}</p>
                )}
              </div>
              <button
                onClick={closeDocument}
                className="text-gray-400 hover:text-gray-600 shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b px-6 gap-4">
              <button
                className={`py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'meta' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                onClick={() => setActiveTab('meta')}
              >
                {t('eurlex.document.tabInfo')}
              </button>
              <button
                className={`py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${activeTab === 'text' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                onClick={() => setActiveTab('text')}
              >
                {t('eurlex.document.tabText')}
                {loadingHtml && <Loader2 className="h-3 w-3 animate-spin" />}
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'meta' && (
                <>
                  {loadingDoc && (
                    <div className="flex items-center justify-center py-12 gap-2">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                      <span>{t('eurlex.document.loading')}</span>
                    </div>
                  )}

                  {!loadingDoc && docMeta && (
                    <div className="space-y-5">
                      {docMeta.error && (
                        <p className="text-amber-600 text-sm bg-amber-50 border border-amber-200 rounded p-3">
                          {docMeta.error}
                        </p>
                      )}

                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                        {docMeta.date && (
                          <div>
                            <dt className="font-medium text-gray-500">{t('eurlex.document.date')}</dt>
                            <dd className="text-gray-900 mt-0.5">{docMeta.date}</dd>
                          </div>
                        )}
                        {docMeta.document_type && (
                          <div>
                            <dt className="font-medium text-gray-500">{t('eurlex.document.type')}</dt>
                            <dd className="text-gray-900 mt-0.5">{docMeta.document_type}</dd>
                          </div>
                        )}
                        {docMeta.authors.length > 0 && (
                          <div className="sm:col-span-2">
                            <dt className="font-medium text-gray-500">{t('eurlex.document.authors')}</dt>
                            <dd className="text-gray-900 mt-0.5">{docMeta.authors.join(', ')}</dd>
                          </div>
                        )}
                        {docMeta.eli && (
                          <div className="sm:col-span-2">
                            <dt className="font-medium text-gray-500">ELI</dt>
                            <dd className="mt-0.5">
                              <a href={docMeta.eli} target="_blank" rel="noopener noreferrer"
                                className="text-blue-600 hover:underline break-all">
                                {docMeta.eli}
                              </a>
                            </dd>
                          </div>
                        )}
                        {isInForce(docMeta.in_force) && (
                          <div>
                            <dt className="font-medium text-gray-500">{t('eurlex.document.status')}</dt>
                            <dd className="mt-0.5">
                              <Badge className="text-xs bg-green-100 text-green-800 border-green-200">
                                <Shield className="h-3 w-3 mr-1" />
                                {t('eurlex.document.inForce')}
                              </Badge>
                            </dd>
                          </div>
                        )}
                      </dl>

                      {docMeta.subjects.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-2">
                            {t('eurlex.document.subjects')}
                          </h4>
                          <div className="flex flex-wrap gap-1.5">
                            {docMeta.subjects.map((s) => (
                              <Badge key={s} variant="outline" className="text-xs text-gray-700">{s}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'text' && (
                <>
                  {loadingHtml && (
                    <div className="flex items-center justify-center py-12 gap-2">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                      <span>{t('eurlex.document.loading')}</span>
                    </div>
                  )}
                  {!loadingHtml && docHtml && (
                    <div
                      className="prose prose-sm max-w-none text-gray-800"
                      dangerouslySetInnerHTML={{ __html: docHtml }}
                    />
                  )}
                  {!loadingHtml && !docHtml && (
                    <div className="text-center py-12 text-gray-500 space-y-3">
                      <p>{t('eurlex.document.noText')}</p>
                      <a
                        href={docMeta?.eurlex_url || selectedDoc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {t('eurlex.document.viewOnEurlex')}
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-between p-4 border-t bg-gray-50">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" asChild>
                  <a
                    href={docMeta?.eurlex_url || selectedDoc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    {t('eurlex.actions.openOnEurlex')}
                  </a>
                </Button>
                {selectedDoc.celex && (
                  <Button variant="outline" size="sm" asChild>
                    <a
                      href={docMeta?.pdf_url || `https://eur-lex.europa.eu/legal-content/EN/TXT/PDF/?uri=CELEX:${selectedDoc.celex}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      {t('eurlex.actions.pdf')}
                    </a>
                  </Button>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={closeDocument}>
                {t('eurlex.actions.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EurlexDashboard;
